import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Descope } from '@descope/react-sdk';
import { useAuth } from '../../services/auth';
import '../../styles/login.css';
 
const Login = () => {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
 
  // If already authenticated, redirect immediately
  useEffect(() => {
    console.log('[Login] Component mounted, isAuthenticated:', isAuthenticated);
   
    if (isAuthenticated && !isProcessing) {
      console.log('[Login] ✅ Already authenticated, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate, isProcessing]);
 
  // Handle successful Descope authentication
  const handleSuccess = async (e) => {
    console.log('[Login] 🎉 Descope authentication successful');
    console.log('[Login] Event details:', e.detail);
   
    setIsProcessing(true);
 
    try {
      // Extract tokens from Descope event
      const sessionToken = e.detail.sessionJwt || e.detail.sessionToken;
      const refreshToken = e.detail.refreshJwt || e.detail.refreshToken;
 
      console.log('[Login] Tokens received:', {
        hasSessionToken: !!sessionToken,
        hasRefreshToken: !!refreshToken
      });
 
      if (!sessionToken) {
        console.error('[Login] ❌ No session token in Descope response');
        setIsProcessing(false);
        return;
      }
 
      // Store tokens immediately in localStorage
      localStorage.setItem('authToken', sessionToken);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
      console.log('[Login] ✅ Tokens stored in localStorage');
 
      // Validate with backend
      console.log('[Login] Validating with backend...');
      const success = await login(sessionToken, refreshToken);
 
      if (success) {
        console.log('[Login] ✅ Backend validation successful');
       
        // Small delay to ensure auth context is updated
        await new Promise(resolve => setTimeout(resolve, 100));
       
        // Navigate to dashboard
        console.log('[Login] Navigating to dashboard...');
        navigate('/dashboard', { replace: true });
      } else {
        console.error('[Login] ❌ Backend validation failed');
        alert('Login failed. Please try again.');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('[Login] ❌ Login error:', error);
      alert('An error occurred during login. Please try again.');
      setIsProcessing(false);
    }
  };
 
  // Handle Descope errors
  const handleError = (e) => {
    console.error('[Login] ❌ Descope error:', e.detail);
    alert(`Login failed: ${e.detail.errorMessage || 'Unknown error'}`);
    setIsProcessing(false);
  };
 
  return (
    <div className="login-container">
      <video
        className="login-background-video"
        autoPlay
        loop
        muted
        playsInline
        src="/a.mp4"
      />
      <div className="login-overlay"></div>
      <div className="login-logo login-logo-left">
        <img
          src="/ironleaflogo.png"
          alt="Ironleaf Logo"
          className="login-logo-image"
        />
      </div>
      <div className="login-logo login-logo-right">
        <img
          src="/AAA_logo.jpeg"
          alt="AAA Logo"
          className="login-logo-image"
        />
      </div>
 
      <div className="login-card">
        <div className="login-form">
          {isProcessing ? (
            <div className="processing-overlay">
              <div className="spinner"></div>
              <p>Completing authentication...</p>
            </div>
          ) : (
            <>
              <h2>🔐 Authentication</h2>
              <p>Login with your credentials:</p>
             
              <Descope
                flowId="migrate-users-from-passwords-to-passkeys-using-a-b"
                onSuccess={handleSuccess}
                onError={handleError}
                theme="light"
              />
            </>
          )}
        </div>
      </div>
 
      <style jsx>{`
        .processing-overlay {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
        }
 
        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }
 
        .processing-overlay p {
          color: #666;
          font-size: 16px;
          margin: 0;
        }
 
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
 
export default Login;